import { useEffect, useRef, useState } from "react";

import storeRecommendationUrl from "@/assets/snr-location-recommendation-model-final.html?url";
import { useTranslation } from "@/store/locale";
import { useThemeStore } from "@/store/theme";

/**
 * The club location recommendation model, delivered as one self-contained HTML
 * document (its own Leaflet map, tabs and scoring tables).
 *
 * **Served from the app's own origin, not from a blob.** It used to be a
 * `?raw` import wrapped in `URL.createObjectURL`, on the reasoning that an
 * opaque origin kept a document we render but do not own from reaching back
 * into the shell. The cost of that was the map: a document loaded from a
 * `blob:` URL sends **no `Referer`** on its subresources, so every tile request
 * reached OpenStreetMap unattributable, and OSM's tile policy answers one it
 * cannot attribute with a 403 image reading "Access blocked — App is not
 * following the tile usage policy". The map drew as a wall of those.
 *
 * `?url` keeps everything the blob was chosen for — Vite emits the file into
 * the build with a hashed name, so it is still part of the bundle and still
 * versions with the app — and gives it a real same-origin URL, which is what
 * makes the tiles load. What it gives up is the opaque origin; the document is
 * our own asset, built and shipped with this app, so that is a boundary we
 * were drawing against ourselves. Sandboxing it back would not do: `sandbox`
 * without `allow-same-origin` is how you get an opaque origin, which is how
 * you get the empty `Referer` back.
 *
 * The theme still travels rather than being read: the initial choice rides in
 * on the frame's fragment, so the document paints right on the first frame, and
 * every later toggle goes over postMessage. Changing the `src` instead would
 * reload the frame and throw away the map view, the open tab and the min-gap
 * filter every time someone flips the switch.
 */
function ClubRecommendationPage() {
  const t = useTranslation();
  const theme = useThemeStore((state) => state.choice) ?? "light";
  const frameRef = useRef<HTMLIFrameElement>(null);

  // Read once: the URL carries the theme the frame *starts* in, and a changed
  // URL would remount the frame.
  const [initialTheme] = useState(theme);

  const postTheme = () => {
    // Same origin now, so the message can name it instead of going to "*".
    frameRef.current?.contentWindow?.postMessage(
      { type: "mrm:theme", theme },
      window.location.origin,
    );
  };

  // A toggle before the document has finished loading would land on nothing,
  // so the frame's load event replays the current choice.
  useEffect(postTheme, [theme]);

  // `h-full`, not `100vh`: the shell's main column is the scroller and the
  // header already takes its share of the viewport, so a viewport-height frame
  // would hang off the bottom and scroll the page under itself.
  return (
    <iframe
      ref={frameRef}
      src={`${storeRecommendationUrl}#theme=${initialTheme}`}
      title={t("nav.clubRecommendation")}
      onLoad={postTheme}
      className="bg-canvas h-full w-full border-0"
    />
  );
}

export default ClubRecommendationPage;
